import React from 'react';
import { fetchUserDetails } from "./services/authAPI";
import { generateTextFromData } from "./services/textGenerationAPI";
import { generateBulletsWithIndent, rgba2hex } from './javascript';
import moment from 'moment';
import { SUPPORT_EMAIL_ID } from "./helper";
import { COMPLEX_PAGE_OPTIONS } from "../containers/ComplexDocumentDesign/translationValues";
import { Tooltip } from 'antd';

const DEFAULT_DATE_FORMAT = `${process.env.REACT_APP_VARIABLE_DATE_FORMAT}`.toUpperCase();

export const isAppliedFormat = value => (
  value !== undefined &&
  value !== false &&
  value !== 'false' &&
  value !== 'normal' &&
  value !== 'none' &&
  value !== '400' &&
  value !== ''
);

const normalizeColorValue = color => (
  color?.startsWith('rgba') ? rgba2hex(color) : color
);

const getFontWeightStyles = (attributes = {}, useBlockFallback = false) => (
  [
    (isAppliedFormat(attributes.bold) || (useBlockFallback && isAppliedFormat(attributes['block-bold']))) && 'bold',
    (isAppliedFormat(attributes.italic) || (useBlockFallback && isAppliedFormat(attributes['block-italic']))) && 'italics',
    (isAppliedFormat(attributes.underline) || (useBlockFallback && isAppliedFormat(attributes['block-underline']))) && 'underline',
    (isAppliedFormat(attributes.strike) || (useBlockFallback && isAppliedFormat(attributes['block-strike']))) && 'strike_through'
  ].filter(Boolean)
);

const applyTextStyleAttributes = (style, attributes = {}, defaultFontFamily, defaultFontSize, useBlockFallback = false) => {
  const appliedFont = attributes.font?.replaceAll('"', '') || (useBlockFallback ? attributes['block-font']?.replaceAll('"', '') : undefined);
  if (appliedFont && appliedFont !== defaultFontFamily) {
    style.fontFamily = appliedFont;
  }

  const fontSize = attributes.size || (useBlockFallback && attributes['block-size']);
  const appliedFontSize = parseFloat(`${fontSize || '0'}`.replaceAll('px', ''));
  if (appliedFontSize !== 0 && appliedFontSize !== defaultFontSize) {
    style.fontSize = appliedFontSize;
  }

  const fontWeight = getFontWeightStyles(attributes, useBlockFallback);
  if (fontWeight.length > 0) {
    style.fontWeight = fontWeight;
  }

  const color = attributes.color || (useBlockFallback && attributes['block-color']);
  if (color) {
    style.color = normalizeColorValue(color);
  }

  const background = attributes.background || (useBlockFallback && attributes['block-background']);
  if (background) {
    style.backgroundColor = normalizeColorValue(background);
  }

  if (attributes.link) {
    style.hyperlinkValue = attributes.link;
  }
  if (attributes.textTransform) {
    style.textTransform = attributes.textTransform;
  }
  if (attributes.script) {
    style.baseline = attributes.script;
  }
  if (attributes.letterspace) {
    style.letterSpacing = parseFloat(`${attributes.letterspace || '0'}`.replaceAll('px', ''));
  }
  if (attributes.heading) {
    style.heading = attributes.heading;
  }
  return style;
};

const applyInlineAttributes = (obj, iter, defaultFontFamily, defaultFontSize) => {
  obj.style = {};
  applyTextStyleAttributes(obj.style, iter.attributes, defaultFontFamily, defaultFontSize);
  if (obj?.contentType === "FUNCTION") {
    delete obj.style.hyperlinkValue;
  }
  if (iter.attributes?.link && obj?.contentType !== "FUNCTION") {
    obj.style.hyperlinkValue = iter.attributes.link;
  }
  return obj;
}

const getLineBreakStyle = (attributes = {}, defaultFontFamily, defaultFontSize, includeTextStyles = false) => {
  const style = {
    alignment: attributes?.align || 'left',
    lineSpacing: parseFloat(`${attributes?.lineheight || '1.5'}`.replaceAll('em', '')),
    list: attributes?.list,
    indent: attributes?.indent || 0,
  };

  const appliedFont = attributes['block-font'] ? attributes['block-font']?.replaceAll('"', '') : defaultFontFamily;
  if (appliedFont !== defaultFontFamily) {
    style.fontFamily = appliedFont;
  }
  if (attributes['block-size']) {
    const blockSize = attributes['block-size'] ? parseFloat(attributes['block-size']?.replaceAll('px', '')) : defaultFontSize;
    if (blockSize !== parseFloat(defaultFontSize)) {
      style.fontSize = blockSize;
    }
  }
  if (attributes['list'] === undefined && style.indent !== 0) {
    const blockSize = attributes['block-size'] ? parseFloat(attributes['block-size']?.replaceAll('px', '')) : defaultFontSize;
    style.textIndent = blockSize * (1.5 + (1 * (attributes.indent || 0)));
  }

  if (includeTextStyles) {
    applyTextStyleAttributes(style, attributes, defaultFontFamily, defaultFontSize, true);
  }

  return style;
};

// INFO : It constructs BE consumable JSON From Quill GetContent()
export const constructAutoSaveJSON = (data, defaultFontFamily, defaultFontSize, globalVariables, allowLocal = false) => {
  const dataSet = data.ops.reduce((acc, op) => {
    if(op.insert.mention) {
      return [...acc, op];
    } else if (op.insert.includes('\n') && op.insert !== '\n') {
      let newLines = op.insert.split('\n');
      newLines = newLines.splice(1);
      let newAcc = acc;
      newLines.forEach(() => {
        newAcc = [...newAcc, Object.assign({}, op, { insert: '\n' })];
      })
      return newAcc;
    } else {
      return [...acc, op];
    }
  }, []);

  let result = [];
  let decimalListIndex = [];

  dataSet.forEach((iter, index) => {
    if (iter.insert.mention) {
      let variableDetails = iter.insert.mention;
      let obj = {};
      if(variableDetails.isLogicMention){
        obj.contents= JSON.parse(variableDetails?.extraInfo);
        obj.content = variableDetails?.placeHolderText;
        obj.contentType= 'FUNCTION';
      }else {
        obj.content= `{${variableDetails.id}}`;
        obj.contentType= 'PARAGRAPH';
      }
      let filteredVar = globalVariables?.filter(
        globalVar => globalVar.placeHolderText === variableDetails.value,
      );
      if (filteredVar && filteredVar.length > 0) {
        filteredVar = filteredVar[0];
      } else {
        filteredVar = undefined;
      }
      if (!filteredVar && allowLocal) {
        obj.content = variableDetails.vname;
      }
      if (iter.attributes) {
        obj = applyInlineAttributes(obj, iter, defaultFontFamily, defaultFontSize);
      }
      obj.style = {
        ...obj.style,
        ...(!variableDetails.isLogicMention && { isVariable: true }),
        ...(variableDetails?.parentEntity && variableDetails?.entityName && {
          parentEntity: variableDetails.parentEntity,
          entityName: variableDetails.entityName,
        })
        
      };
      if (allowLocal && !filteredVar) {
        obj.style.variableType = variableDetails?.variableType;
      }
      const variableType = filteredVar?.type || variableDetails?.variableType;
      if(variableType === 'Date') {
        obj.style.dateFormat = variableDetails?.dateFormat
      } else if (variableType === 'ListString') {
        obj.contentType = 'LIST_VARIABLE'
      }
      result.push(obj);
    } else if (iter.insert === '\n') {
        let previousLineBreakIndex = undefined;
        for (let i = result.length - 1; i >= 0; i--) {
          if (result[i].contentType === 'LINE_BREAK') {
            previousLineBreakIndex = i;
            break;
          }
        }
        const normalizedPreviousLineBreakIndex = previousLineBreakIndex === undefined ? -1 : previousLineBreakIndex;
        const currentLineHasContent = result.length > normalizedPreviousLineBreakIndex + 1;
        const lineBreakStyle = iter.attributes
          ? getLineBreakStyle(iter.attributes, defaultFontFamily, defaultFontSize, !currentLineHasContent)
          : undefined;
        if (iter?.attributes?.list) {
          let listLabelObj = {
            contentType: 'LIST_LABEL',
          }
          if (previousLineBreakIndex !== undefined && iter.attributes.list === result[previousLineBreakIndex].style?.list) {
            if (iter.attributes.list === 'bullet') {
              listLabelObj.content = '•';
            } else {
              const currentIndent = iter.attributes.indent || 0;
              // console.log("List identified :: ", currentIndent);
              if (currentIndent === result[previousLineBreakIndex].style.indent) {
                const lastDecimalNoted = decimalListIndex.pop();
                // console.log("Coming here 1 :: ", lastDecimalNoted);
                listLabelObj.content = generateBulletsWithIndent(lastDecimalNoted.count + 1, iter.attributes.indent || 0);
                decimalListIndex.push(Object.assign({}, lastDecimalNoted, { count: lastDecimalNoted.count + 1 }));
              } else if (currentIndent > result[previousLineBreakIndex].style.indent) {
                // console.log("Coming here 2 :: ", JSON.parse(JSON.stringify(decimalListIndex)));
                let diff = currentIndent - result[previousLineBreakIndex].style.indent - 1;
                while (diff > 0) {
                  decimalListIndex.push({ count: -1, indent: currentIndent - diff });
                  diff--;
                }
                decimalListIndex.push({ count: 0, indent: iter.attributes.indent });
                listLabelObj.content = generateBulletsWithIndent(0, iter.attributes.indent || 0);
              } else if (currentIndent < result[previousLineBreakIndex].style.indent) {
                let lastDecimalNoted = decimalListIndex.pop();
                while (currentIndent < lastDecimalNoted.indent) {
                  lastDecimalNoted = decimalListIndex.pop();
                }
                listLabelObj.content = generateBulletsWithIndent(lastDecimalNoted.count + 1, iter.attributes.indent || 0);
                decimalListIndex.push(Object.assign({}, lastDecimalNoted, { count: lastDecimalNoted.count + 1 }));
              }
            }
          } else {
            // There has been no breaks in the content before the first list item
            if (previousLineBreakIndex === undefined) {
              previousLineBreakIndex = -1;
            }
            if (iter.attributes.list === 'ordered') {
              decimalListIndex = []; // Start a new list
              const newIndex = iter?.attributes?.['block-start'] ? Number(iter?.attributes?.['block-start']) - 1 : 0
              decimalListIndex.push({ count: newIndex, indent: iter.attributes.indent || 0 });
              listLabelObj.content = generateBulletsWithIndent(newIndex, iter.attributes.indent || 0);
            } else {
              listLabelObj.content = '•';
            }
          }

          listLabelObj.style = { indent: 0 }; // TODO : Check if attributes has to be passed from the paragraph item
          if (result[previousLineBreakIndex + 1]) {
            listLabelObj.style = Object.assign({}, result[previousLineBreakIndex + 1].style, {
              textIndent: (result[previousLineBreakIndex + 1].style?.fontSize || defaultFontSize) * (1.5 + (1 * (iter.attributes.indent || 0))),
              indent: iter.attributes.indent || 0,
              isVariable: false,
              heading: undefined,
              hyperlinkValue: undefined
            });
          } else if (lineBreakStyle) {
            listLabelObj.style = Object.assign({}, lineBreakStyle, {
              textIndent: (lineBreakStyle.fontSize || defaultFontSize) * (1.5 + (1 * (iter.attributes.indent || 0))),
              indent: iter.attributes.indent || 0,
              isVariable: false,
              heading: undefined,
              hyperlinkValue: undefined
            });
          }
          if (result[previousLineBreakIndex + 1] && result[previousLineBreakIndex + 1].content === listLabelObj?.content) {
            result.splice(previousLineBreakIndex + 1, 1, listLabelObj);
          } else  {
            result.splice(previousLineBreakIndex + 1, 0, listLabelObj);
          }
        }
          let obj = {
            content: '\n',
            contentType: 'LINE_BREAK',
          };

          if (iter.attributes) {
            obj.style = lineBreakStyle;
          }
          result.push(obj);
    } else {
      let obj = {
        content: iter.insert.replaceAll('\t', '    '),
        contentType: 'PARAGRAPH',
      };
      if (iter.attributes) {
        obj = applyInlineAttributes(obj, iter, defaultFontFamily, defaultFontSize);
      } else {
        obj.style= {
          color: '#000000'
        }
      }
      result.push(obj);
    }
  });
  return result;
};

// INFO : It constructs FE consumable JSON From BE Response
export const constructQuillJSON = (data, defaultFontFamily, defaultFontSize, globalVariables, isPreview = false, dataSet = undefined, allowLocal = false) => {
  if (!data || data.length === 0) {
    return undefined;
  }
  let result = [];
  let listLabelStyles = {};
  let filteredVar;

  data.forEach((inst, index) => {
    let isRelationShip = false;
    if (inst.contentType === 'LIST_LABEL') {
      const blockStart = parseInt(inst.content.replace('.', ''), 10);
      listLabelStyles = Object.assign({}, inst.style, {
        'block-font': inst.style?.fontFamily?.replaceAll('"', '') || defaultFontFamily,
        'block-size': `${inst.style?.fontSize ? inst.style?.fontSize : defaultFontSize}px`,
        'block-bold': inst.style?.fontWeight?.includes('bold') ? '700' : 'normal',
        'block-italic': inst.style?.fontWeight?.includes('italics') ? 'italic' : 'normal',
        'block-underline': inst.style?.fontWeight?.includes('underline') ? 'true' : 'false',
        'block-strike': inst.style?.fontWeight?.includes('strike_through') ? 'true' : 'false',
        'block-color': inst.style?.color || '#000000',
        'block-background': inst.style?.backgroundColor || '#ffffff00',
        'block-start': typeof blockStart === 'number' && !isNaN(blockStart) ? blockStart : undefined
      });
      return;
    }
    if (inst.style?.isVariable) {
      filteredVar = globalVariables?.filter(
        globalVar => globalVar.id === parseInt(inst.content?.toString().replaceAll("{","").replaceAll("}", "")),
      );
      if (filteredVar && filteredVar.length > 0) {
        filteredVar = filteredVar[0];
      } else if (allowLocal) {
        filteredVar = {
          name: inst.content,
          placeHolderText: inst.content,
          type: inst.style?.variableType
        }
      }
      if(filteredVar.type === 'html') {
        inst.style.type = 'html';
      }
      if(inst.style?.parentEntity && inst.style?.entityName ){
        isRelationShip = true
      }
    }
    let variableName = filteredVar?.placeHolderText;

    let repeatedDataSet;
    if(dataSet !==undefined && inst.style?.isVariable && inst.repeatBlockName !== undefined && inst.repeatBlockIndex !== undefined){
      repeatedDataSet = dataSet[inst.repeatBlockName][inst.repeatBlockIndex];
    }

    if (inst.style?.isVariable && isPreview && dataSet !== undefined) {
      const variableAttributes = {
        font: inst.style?.fontFamily?.replaceAll('"', '') || defaultFontFamily,
        size: `${inst.style?.fontSize ? inst.style?.fontSize : defaultFontSize}px`,
        bold: inst.style?.fontWeight?.includes('bold'),
        italic: inst.style?.fontWeight?.includes('italics'),
        underline: inst.style?.fontWeight?.includes('underline'),
        strike: inst.style?.fontWeight?.includes('strike_through'),
        color: inst.style?.color,
        background: inst.style?.backgroundColor,
        assignedVariable: inst.style?.assignedVariable,
        textTransform: inst.style?.textTransform,
        letterspace: inst.style?.letterSpacing ? `${inst.style?.letterSpacing}px` : 0,
        link: inst.style?.hyperlinkValue,
        script: inst.style?.baseline
      };
      if (filteredVar?.type === 'ListString') {
        const listItems =  repeatedDataSet !== undefined
        ? repeatedDataSet[variableName]
        : dataSet[variableName];
        listItems.forEach((listItem, index) => {
          result.push({
            insert: listItem,
            attributes: variableAttributes
          });
          if (index < listItems.length - 1) {
            result.push({
              insert: '\n',
              attributes: {
                align: inst.style?.alignment || 'left',
                lineheight: `${inst.style?.lineSpacing || 1.5}em`,
                list: "bullet",
                indent: 0,
                'block-font': inst.style?.fontFamily?.replaceAll('"', '') || defaultFontFamily,
                'block-size': `${inst.style?.fontSize ? inst.style?.fontSize : defaultFontSize}px`,
                'block-bold': inst.style?.fontWeight?.includes('bold') ? '700' : 'normal',
                'block-italic': inst.style?.fontWeight?.includes('italics') ? 'italic' : 'normal',
                'block-color': inst.style?.color || '#000000',
                'block-background': inst.style?.backgroundColor || '#ffffff00',
              }
            });
          } else {
            listLabelStyles = {
              list: "bullet",
              index: 0
            }
          }
        });
      } else {
        let variableText =
          repeatedDataSet !== undefined
            ? repeatedDataSet[variableName]
            : dataSet[variableName];
        result.push({
          insert: filteredVar?.type === 'Date'
            ? moment(variableText, DEFAULT_DATE_FORMAT).format(inst?.style?.dateFormat?.toUpperCase())
            : variableText,
          attributes: variableAttributes
        });
      }
    } else {
      let tooltipTitle = '';
      if (inst.content === 'custom_logic') {
        const loopContents = inst.contents;
        for (let i = 0; i < loopContents.length; i++) {
          if (loopContents[i].style?.isVariable === true) {
            const variableId = Number(loopContents[i].content.replace(/\D/g, ''));
            const variableDetails = globalVariables.filter(globalVar => globalVar.id === variableId)[0];
            tooltipTitle += variableDetails?.placeHolderText;
          }
          else {
            tooltipTitle += loopContents[i].content;
          }
        }
      }
      result.push({
        insert:
          inst.contentType === 'LINE_BREAK'
            ? '\n'
            : inst.contentType === 'FUNCTION'
            ? 
            {
                mention: {
                  denotationChar: '@',
                  value: inst.content,
                  extraInfo: inst.contents,
                  isLogicMention: true,
                  placeHolderText: inst.content,
                  tooltipTitle: tooltipTitle
                }
              }
            : inst.style?.isVariable
            ? {
                mention: {
                  denotationChar: '$',
                  value: isPreview && dataSet !== undefined ? dataSet[variableName] : filteredVar?.displayName ? filteredVar?.displayName : variableName,
                  vtype: 'assigned',
                  vname: isPreview && dataSet !== undefined ? dataSet[variableName] : filteredVar?.displayName ? filteredVar?.displayName : variableName,
                  id: filteredVar?.id,
                  // styles: inst.style, // TODO : Discuss how this is carried to mention
                  dateFormat: filteredVar?.type === 'Date' && inst?.style?.dateFormat,
                  variableType: filteredVar?.type,
                  count: filteredVar?.count,
                  length: filteredVar?.length,
                  isComplexDesign: filteredVar?.type === 'ListString',
                  isPreview,
                  placeHolderText: isRelationShip ? `${inst.style?.parentEntity}.${filteredVar?.placeHolderText}` : filteredVar?.placeHolderText,
                  ...(isRelationShip && { parentEntity : inst.style?.parentEntity, entityName : inst.style?.entityName })
                },
              }
            : inst.content,
        attributes:
          inst.contentType === 'LINE_BREAK'
            ? {
                align: inst.style?.alignment || 'left',
                lineheight: `${inst.style?.lineSpacing || 1.5}em`,
                list: inst.style?.list,
                indent: inst.style?.indent,
                'block-font': inst.style?.fontFamily?.replaceAll('"', '') || defaultFontFamily,
                'block-size': `${inst.style?.fontSize ? inst.style?.fontSize : defaultFontSize}px`,
                'block-bold': inst.style?.fontWeight?.includes('bold') ? '700' : 'normal',
                'block-italic': inst.style?.fontWeight?.includes('italics') ? 'italic' : 'normal',
                'block-color': inst.style?.color || '#000000',
                'block-background': inst.style?.backgroundColor || '#ffffff00',
                ...listLabelStyles,
              }
            : inst.contentType === 'FUNCTION'
            ? {
                ...inst.style,
                font: inst.style?.fontFamily?.replaceAll('"', '') || defaultFontFamily,
                size: `${inst.style?.fontSize ? inst.style?.fontSize : defaultFontSize}px`,
                bold: inst.style?.fontWeight?.includes('bold'),
                italic: inst.style?.fontWeight?.includes('italics'),
                underline: inst.style?.fontWeight?.includes('underline'),
                strike: inst.style?.fontWeight?.includes('strike_through'),
                color: inst.style?.color,
                background: inst.style?.backgroundColor,
                textTransform: inst.style?.textTransform,
                letterspace: inst.style?.letterSpacing ? `${inst.style?.letterSpacing}px` : 0,
                script: inst.style?.baseline,
              }
            : inst.style?.isVariable
            ? {
                ...inst.style,
                font: inst.style?.fontFamily?.replaceAll('"', '') || defaultFontFamily,
                size: `${inst.style?.fontSize ? inst.style?.fontSize : defaultFontSize}px`,
                bold: inst.style?.fontWeight?.includes('bold'),
                italic: inst.style?.fontWeight?.includes('italics'),
                underline: inst.style?.fontWeight?.includes('underline'),
                strike: inst.style?.fontWeight?.includes('strike_through'),
                textTransform: inst.style?.textTransform,
                letterspace: inst.style?.letterSpacing && parseInt(`${inst.style?.letterSpacing}`.replaceAll('px', ''), 10),
                link: inst.style?.hyperlinkValue,
                script: inst.style?.baseline
              }
            : {
                font: inst.style?.fontFamily?.replaceAll('"', '') || defaultFontFamily,
                size: `${inst.style?.fontSize ? inst.style?.fontSize : defaultFontSize}px`,
                bold: inst.style?.fontWeight?.includes('bold'),
                italic: inst.style?.fontWeight?.includes('italics'),
                underline: inst.style?.fontWeight?.includes('underline'),
                strike: inst.style?.fontWeight?.includes('strike_through'),
                color: inst.style?.color,
                background: inst.style?.backgroundColor,
                assignedVariable: inst.style?.assignedVariable,
                textTransform: inst.style?.textTransform,
                script: inst.style?.baseline,
                letterspace: inst.style?.letterSpacing ? `${inst.style?.letterSpacing}px` : 0,
                link: inst.style?.hyperlinkValue,
                heading: inst.style?.heading,
                summary: inst.style?.summary
              },
      });
    }
    if (inst.contentType === 'LINE_BREAK') {
      listLabelStyles = {};
    }
  });

  return {
    ops: result,
  };
};



export const generateAndInsertText = async (t, editor, callBack) => {
  try {
    const selection = editor.getSelection();
    let response = await generateTextFromData(editor.getText(selection));
    if (response && (response.errors.length > 0 || ![2204, 2201].includes(response.statusCode))) {
      console.error(response?.errors[0]?.description);
      callBack && callBack(true);
    } else if (response.statusCode === 2204) {
      const response = await fetchUserDetails();
      if(response && response.statusCode === 1903) {
        const planName = response.data?.planMapping?.planName;
        const message = planName === 'Premium'
        ? t(COMPLEX_PAGE_OPTIONS.PREMIUM_PLAN_TEXT_GENERATION_LIMIT_REACHED.key, COMPLEX_PAGE_OPTIONS.PREMIUM_PLAN_TEXT_GENERATION_LIMIT_REACHED.value, { emailId: SUPPORT_EMAIL_ID })
          : planName === 'Free'
        ? t(COMPLEX_PAGE_OPTIONS.AI_TEXT_GENERATE_FREE_PLAN_LIMIT_EXCEEDED_MESSAGE.key, COMPLEX_PAGE_OPTIONS.AI_TEXT_GENERATE_FREE_PLAN_LIMIT_EXCEEDED_MESSAGE.value)
          : t(COMPLEX_PAGE_OPTIONS.AI_TEXT_GENERATE_PREMIUM_PLAN_LIMIT_EXCEEDED_MESSAGE.key, COMPLEX_PAGE_OPTIONS.AI_TEXT_GENERATE_PREMIUM_PLAN_LIMIT_EXCEEDED_MESSAGE.value);
        callBack && callBack(false, message);
      } else {
        callBack && callBack(true);
      }
    } else if (response.statusCode === 2201) {
      callBack && callBack();
      const str = new DOMParser().parseFromString(response.data.choices[0].text, 'text/html')
        .documentElement.textContent;
      const newWritingIndex = selection.index + selection.length;
      const newFormat = editor.getFormat(selection);
      editor.insertText(newWritingIndex + 1, '\n', newFormat, 'user');
      editor.insertText(newWritingIndex + 2, `${str}\n`, newFormat, 'user');
      if (window.getSelection) {
        window.getSelection().removeAllRanges();
      }
    }
  } catch (e) {
    console.log(e);
    callBack && callBack(true);
  }
};

export const sanitizeUrl = (url) => {
  if (!url) return url;
  const hasPrefix = url.startsWith('http://') || url.startsWith('https://');
  const isVariableLink = url.startsWith('{') && url.endsWith('}');
  const sanitizedUrl = isVariableLink ? 'javascript:void(0)' : hasPrefix ? url : 'https://' + url;
  return{ sanitizedUrl, isVariableLink}
};

const getLeafNode = (content, index, defaultFontFamily, defaultFontSize, globalVariables, appendedChild, dataSet = undefined) => {
  const isFunction = content.contentType === 'FUNCTION';
  let leafNodeType = content.style?.fontWeight?.includes('bold')
    ? 'strong'
    :  content.style?.fontWeight?.includes('italics')
      ? 'em'
      : content.style?.fontWeight?.includes('underline')
        ? 'u'
        : content.style?.fontWeight?.includes('strike_through')
          ? 's'
            : content.style?.hyperlinkValue !== undefined
              ? 'a'
                : content.style?.baseline !== undefined
                  ? (content.style?.baseline === 'sub' ? 'sub' : 'sup')
                  : 'span';
  let leafStyles = {
    fontFamily: content.style?.fontFamily?.replaceAll('"', '') || defaultFontFamily,
    fontSize: `${content.style?.fontSize ? content.style?.fontSize : defaultFontSize}px`,
    color: content.style?.color,
    background: content.style?.backgroundColor,
    textTransform: content.style?.textTransform,
    letterSpacing: content.style?.letterSpacing ? `${content.style?.letterSpacing}px` : 0
  }
  let extraProps = {};
  let displayNameVar = null;
  let sanitizedLinkData = null;
  if (content.style?.hyperlinkValue !== undefined) {
    sanitizedLinkData = sanitizeUrl(content.style?.hyperlinkValue);
  }
  if (leafNodeType === 'a') {
    const { sanitizedUrl, isVariableLink } = sanitizedLinkData || {};
    extraProps.href = sanitizedUrl;
    extraProps.target = isVariableLink ? "_self" : "_blank";
    extraProps.rel = "noopener noreferrer";
  }
  let contentOfNode = content.content;
   if (isFunction) {
    contentOfNode = `@func(${content.content})`;
    leafStyles.background = '#DAC7FF';
    if(content.content === "custom_logic")
    {
      let combinedContent = '';
      const loopContents = content.contents;
      if (loopContents.length > 1) {
        for (let i = 0; i < loopContents.length; i++) {
          if (loopContents[i]?.style?.isVariable === true) {
            const variableId = Number(loopContents[i].content.replace(/\D/g, ''));
            const variableDetails = globalVariables.filter(globalVar => globalVar.id === variableId)[0];
            combinedContent += variableDetails?.placeHolderText;
          }
          else {
            combinedContent += loopContents[i].content;
          }
        }
      }
      displayNameVar = {
        placeHolderText: combinedContent
      }
    }
    else {
      displayNameVar = {
        placeHolderText: content?.content
      }
    }
  }
  if (content.style?.isVariable) {
    const isRelationShip = content.style?.parentEntity && content.style?.entityName;
    const variableId = parseInt(content.content.toString().replaceAll('{', '').replaceAll('}', ''));
    const variableDetails = globalVariables.filter(globalVar => globalVar.id === variableId)[0];
    if(variableDetails?.type === 'html') {
      content.style.type = 'html';
    }
    if (dataSet && (isRelationShip || dataSet[variableDetails?.placeHolderText])) {
      if (isRelationShip && dataSet[variableDetails?.placeHolderText] === undefined) {
        let nestedStructure = content.style?.parentEntity?.split('.').slice(1);
        nestedStructure = nestedStructure.concat(content.style?.entityName);
        contentOfNode = '';
        contentOfNode = dataSet;
        let i = 0;
        while (contentOfNode && i < nestedStructure.length) {
          contentOfNode = contentOfNode[nestedStructure[i]];
          i++;
        }
        if (contentOfNode) {
          contentOfNode = contentOfNode[variableDetails?.placeHolderText];
        } else {
          contentOfNode = '';
        }
      } else {
        contentOfNode = dataSet[variableDetails?.placeHolderText];
      }
    } else {
      if (variableDetails) {
        displayNameVar = {
          displayName: variableDetails?.displayName || variableDetails.placeHolderText,
          placeHolderText: isRelationShip ? `${content.style?.parentEntity}.${variableDetails.placeHolderText}` : variableDetails.placeHolderText
        }
        contentOfNode = variableDetails?.displayName
          ? `$\{${variableDetails.displayName}}`
          : `$\{${variableDetails.placeHolderText}}`;
      } else {
        contentOfNode = `\$\{${content.content}\}`;
      }
      leafStyles.background = 'rgb(211,225,235)';
    }
  }
  if (appendedChild) {
    contentOfNode = React.createElement(appendedChild.type, appendedChild.props, appendedChild.innerHTML);
  }
  let leafNode = React.createElement(leafNodeType, {
    'data-op-index': index,
    style: leafStyles,
    ...extraProps
  }, contentOfNode);
  if (leafNodeType === 'strong') {
    if (content.style?.fontWeight?.includes('italics')) {
      leafNode = React.createElement('em', { 'data-op-index': index, style: leafStyles }, leafNode);
    }
    if (content.style?.fontWeight?.includes('underline')) {
      leafNode = React.createElement('u', { 'data-op-index': index, style: leafStyles }, leafNode);
    }
    if (content.style?.fontWeight?.includes('strike_through')) {
      leafNode = React.createElement('s', { 'data-op-index': index, style: leafStyles }, leafNode);
    }
    if (sanitizedLinkData) {
      const { sanitizedUrl, isVariableLink } = sanitizedLinkData;
      leafNode = React.createElement('a', { 'data-op-index': index, style: leafStyles, href: sanitizedUrl, target: isVariableLink ? "_self" : "_blank", rel: 'noopener noreferrer' }, leafNode);
    }
  } else if (leafNodeType === 'em') {
    if (content.style?.fontWeight?.includes('underline')) {
      leafNode = React.createElement('u', { 'data-op-index': index, style: leafStyles }, leafNode);
    }
    if (content.style?.fontWeight?.includes('strike_through')) {
      leafNode = React.createElement('s', { 'data-op-index': index, style: leafStyles }, leafNode);
    }
    if (sanitizedLinkData) {
      const { sanitizedUrl, isVariableLink } = sanitizedLinkData;
      leafNode = React.createElement('a', { 'data-op-index': index, style: leafStyles, href: sanitizedUrl, target: isVariableLink ? "_self" : "_blank", rel: 'noopener noreferrer' }, leafNode);
    }
  } else if (leafNodeType === 'u') {
    if (content.style?.fontWeight?.includes('strike_through')) {
      leafNode = React.createElement('s', { 'data-op-index': index, style: leafStyles }, leafNode);
    }
    if (sanitizedLinkData) {
      const { sanitizedUrl, isVariableLink } = sanitizedLinkData;
      leafNode = React.createElement('a', { 'data-op-index': index, style: leafStyles, href: sanitizedUrl, target: isVariableLink ? "_self" : "_blank", rel: 'noopener noreferrer' }, leafNode);
    }
  } else if (leafNodeType === 's') {
    if (sanitizedLinkData) {
      const { sanitizedUrl, isVariableLink } = sanitizedLinkData;
      leafNode = React.createElement('a', { 'data-op-index': index, style: leafStyles, href: sanitizedUrl, target: isVariableLink ? "_self" : "_blank", rel: 'noopener noreferrer' }, leafNode);
    }
  }
  return (content.style?.isVariable || isFunction) ? 
    React.createElement(
      Tooltip,
      { title: displayNameVar?.placeHolderText },
      leafNode
    ) : leafNode;
}

function getRowCol(uuid) {
  if (!uuid) return {};
  const parts = uuid.split("-");

  if (parts.length < 3) {
    throw new Error("Invalid uuid format");
  }

  const row = Number(parts[parts.length - 2]);
  const col = Number(parts[parts.length - 1]);

  return { row, col };
}

export const convertContractToReact = (textContent, defaultFontFamily, defaultFontSize, globalVariables, dataSet = undefined, isSerialized, uuid, rowSpan) => {
  const lines = [];
  const {row = null, col = null} = isSerialized ? getRowCol(uuid) : {};
  lines.push({ "elements" : [], style: {} });
  let linesIndex = 0;
  let applyListLabel = undefined;
  textContent.forEach((content, index) => {
    if (content.contentType === 'LIST_LABEL') {
      applyListLabel = content;
    } else if (content.contentType === 'LINE_BREAK') {
      if (index === 0 || textContent[index - 1].contentType === 'LINE_BREAK') {
        lines[linesIndex].elements.push(
          React.createElement('br', { 'data-op-index': index })
        );
      }
      lines[linesIndex].style = {
        lineHeight: `${content.style?.lineSpacing || '1.5'}em`,
        '--font-family': (applyListLabel?.style?.fontFamily || content.style?.fontFamily)?.replaceAll('"', '') || defaultFontFamily,
        '--font-size': `${(applyListLabel?.style?.fontSize || content.style?.fontSize) ? (applyListLabel?.style?.fontSize || content.style?.fontSize) : defaultFontSize}px`,
        '--bold': (applyListLabel?.style?.fontWeight || content.style?.fontWeight)?.includes('bold') ? '700' : 'normal',
        '--italic': (applyListLabel?.style?.fontWeight || content.style?.fontWeight)?.includes('italics') ? 'italic' : 'normal',
        '--underline': (applyListLabel?.style?.fontWeight || content.style?.fontWeight)?.includes('underline') ? 'true' : 'false',
        '--strike': (applyListLabel?.style?.fontWeight || content.style?.fontWeight)?.includes('strike_through') ? 'true' : 'false',
        '--color': applyListLabel?.style?.color || content.style?.color || '#000000',
        '--background': applyListLabel?.style?.backgroundColor || content.style?.backgroundColor || '#ffffff00',
        indent: content.style?.indent || 0
      };
      lines[linesIndex].className = (content.style?.alignment || 'left') !== 'left' ? `ql-align-${content.style?.alignment}` : '';
      if (applyListLabel) {
        lines[linesIndex].style = Object.assign({}, lines[linesIndex].style, {
          'data-list-label': `${applyListLabel.content}`
        });
        applyListLabel = undefined;
      }
      lines[linesIndex].list = lines[linesIndex].list || content.style?.list;
      lines[linesIndex].indent = lines[linesIndex].indent || content.style?.indent;
      
      // Add empty text node for list items without content to ensure they render
      if (content.style?.list && lines[linesIndex].elements.length === 0) {
        lines[linesIndex].elements.push(
          React.createElement('span', { 'data-op-index': index }, '\u00A0') 
        );
      }
      
      lines.push({ "elements": [], style: {} });
      linesIndex += 1;
    } else if (content.contentType === 'LIST_VARIABLE' && content.style.isVariable) { // TODO : Handle text overflow in list variable
      const variableId = parseInt(content.content.replaceAll("{", "").replaceAll("}", ""));
      const variableDetails = globalVariables.filter(globalVar => globalVar.id === variableId)[0];
      const listString = `${Array(5)
        .fill(0)
        .map((a, i) => {
          return ` \u2022 ${(variableDetails?.displayName ? variableDetails.displayName : variableDetails.placeHolderText) + (i + 1)}`;
        })
        .join(`\n`)}`;

      const leafNode = getLeafNode(
        content,
        index,
        defaultFontFamily,
        defaultFontSize,
        globalVariables,
        { type: 'span', props: { 'data-list': listString }, innerHTML: listString },
        dataSet
      );
      lines[linesIndex].elements.push(leafNode);
    } else if(isSerialized && content.contentType === 'PARAGRAPH' && content.content === '1') {
      const newContent = {...content, content: Math.floor((row) / rowSpan) + 1};
      const leafNode = getLeafNode(newContent, index, defaultFontFamily, defaultFontSize, globalVariables, undefined, dataSet);
      lines[linesIndex].elements.push(leafNode);
    } else{
      const leafNode = getLeafNode(content, index, defaultFontFamily, defaultFontSize, globalVariables, undefined, dataSet);
      lines[linesIndex].elements.push(leafNode);
    }
  });
  let blockDOM = undefined;
  const contentsOfQuill = lines.reduce((acc, line, index) => {
    let newDOM = [];
    if (line.elements.length === 0 && !line.list) {
      return acc;
    };
    let className = line.className;
    if (line.indent !== 0) {
      className = `${className} ql-indent-${line.indent}`;
    }
    if (line.list !== undefined) {
      if (blockDOM === undefined) {
        blockDOM = {
          type: line.list === 'ordered' ? 'ol' : 'ul',
          props: {},
          contents: []
        };
      }
      blockDOM.contents.push(React.createElement('li', {
        'data-line-index': index,
        'data-list-label': line.style['data-list-label'] || '', // TODO : Handle labels in overflow within list block
        style: line.style,
        className
      }, ...line.elements));
    } else {
      if (blockDOM !== undefined) {
        newDOM.push(React.createElement(blockDOM.type, blockDOM.props, ...blockDOM.contents));
        blockDOM = undefined;
      }
      newDOM.push(React.createElement('p', { 'data-line-index': index, style: line.style, className: className }, ...line.elements));
    }
    return [...acc, ...newDOM]
  }, []);
  if (blockDOM !== undefined) {  // Handle cases with unfinished ordered block
    console.log("Handle cases with unfinished ordered block !!!");
    let newDOM = [];
    newDOM.push(React.createElement(blockDOM.type, blockDOM.props, ...blockDOM.contents));
    contentsOfQuill.push(newDOM);
  }
  return React.createElement('div', { className: 'ql-editor follow-bullets' }, ...contentsOfQuill);
}

export const getBounds = (node, offset) => {
  const range = document.createRange();
    range.setStart(node, offset);
    range.setEnd(node, offset + 1);
  return range.getBoundingClientRect();
}

export const getAllTextNodesWithinElement = (el) => {
  const children = [];
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  while(walker.nextNode()) {
    children.push(walker.currentNode);
  }
  return children;
}
